import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRightArrowLeft, faCaretUp, faCaretDown } from '@fortawesome/free-solid-svg-icons';
import styles from '../styles/marketdata/CurrencyConverter.module.scss';
import RateHistoryChart from './RateHistoryChart';
import {
  fetchRates,
  loadCachedRates,
  convert,
  availableCurrencies,
} from '../utils/marketdata/currency';

const POPULAR = ['eur', 'usd', 'gbp', 'chf'];

/*
 * `compact` — varianta pentru ecranul principal: fără graficul de 30 de zile.
 * Pe home convertorul e un cârlig de frecvență, se citește dintr-o privire; un
 * grafic acolo ar însemna o cerere de rețea în plus la fiecare deschidere a
 * aplicației, pentru ceva ce nimeni nu s-a oprit să citească. Pagina /valutar
 * primește varianta completă.
 */
const CurrencyConverter = ({ compact = false }) => {
  // Prima randare = la fel ca pe server (fara date), altfel React da eroarea de
  // hidratare #418: serverul n-are localStorage. Cache-ul se aplica imediat dupa
  // montare (un singur cadru), apoi refresh de retea.
  const [rates, setRates] = useState(null);
  const [fromCache, setFromCache] = useState(true);
  const [loading, setLoading] = useState(true);

  const [amount, setAmount] = useState('100');
  const [from, setFrom] = useState('eur');
  const [to, setTo] = useState('ron');

  useEffect(() => {
    let alive = true;
    const cached = loadCachedRates();
    if (cached) setRates(cached);
    fetchRates().then((res) => {
      if (!alive) return;
      if (res) {
        setRates(res.data);
        setFromCache(res.fromCache);
      }
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, []);

  const currencies = useMemo(
    () => (rates ? availableCurrencies(rates.rates) : []),
    [rates]
  );

  const numericAmount = parseFloat((amount || '').toString().replace(',', '.'));
  const result =
    rates && !Number.isNaN(numericAmount)
      ? convert(numericAmount, from, to, rates.rates)
      : null;

  const swap = () => {
    setFrom(to);
    setTo(from);
  };

  // Graficul arată întotdeauna valuta față de RON. Dacă perechea e RON↔ceva,
  // luăm „celălalt"; dacă ambele sunt RON, nu are ce să arate.
  const chartCurrency = from !== 'ron' ? from : to !== 'ron' ? to : null;

  const fmt = (n, digits = 2) =>
    new Intl.NumberFormat('ro-RO', {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(n);

  const renderChange = (cur) => {
    if (!rates || !rates.change || rates.change[cur] === undefined) return null;
    const c = rates.change[cur];
    if (c === '-' || c === 0) return null;
    const up = Number(c) > 0;
    return (
      <span className={up ? styles.up : styles.down}>
        <FontAwesomeIcon icon={up ? faCaretUp : faCaretDown} height="12" width="12" />
        {Math.abs(Number(c)).toFixed(2)}%
      </span>
    );
  };

  if (!rates && loading) {
    return <div className={styles.loading}>Se încarcă cursul valutar…</div>;
  }

  if (!rates) {
    return (
      <div className={styles.loading}>
        Cursul valutar nu este disponibil momentan. Verifică-ți conexiunea.
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.card}>
        <div className={styles.field}>
          <label>Sumă</label>
          <div className={styles.inputRow}>
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9.,]/g, ''))}
              aria-label="Sumă de convertit"
            />
            <select value={from} onChange={(e) => setFrom(e.target.value)} aria-label="Din valuta">
              {currencies.map((c) => (
                <option key={c} value={c}>
                  {c.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
        </div>

        <button type="button" className={styles.swap} onClick={swap} aria-label="Inversează valutele">
          <FontAwesomeIcon icon={faArrowRightArrowLeft} height="16" width="16" />
        </button>

        <div className={styles.field}>
          <label>Rezultat</label>
          <div className={styles.resultRow}>
            <div className={styles.resultValue} aria-live="polite">
              {result !== null ? fmt(result) : '—'}
            </div>
            <select value={to} onChange={(e) => setTo(e.target.value)} aria-label="În valuta">
              {currencies.map((c) => (
                <option key={c} value={c}>
                  {c.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.rateLine}>
          {result !== null && (
            <span>
              1 {from.toUpperCase()} = {fmt(convert(1, from, to, rates.rates), 4)} {to.toUpperCase()}
            </span>
          )}
        </div>
      </div>

      <div className={styles.stats}>
        {POPULAR.filter((c) => rates.rates[c]).map((c) => (
          <button
            key={c}
            type="button"
            className={`${styles.stat} ${from === c ? styles.statActive : ''}`}
            onClick={() => setFrom(c)}
            aria-label={`Convertește din ${c.toUpperCase()}`}
          >
            <span className={styles.statBadge}>{c.toUpperCase()}</span>
            <span className={styles.statValue}>{fmt(rates.rates[c], 4)}</span>
            <span className={styles.statLabel}>LEI {renderChange(c)}</span>
          </button>
        ))}
      </div>

      {!compact && chartCurrency && <RateHistoryChart currency={chartCurrency} />}

      <div className={styles.meta}>
        Curs de referință BNR{rates.date ? ` · ${rates.date.replace(/-/g, '.')}` : ''}
        {fromCache && <span className={styles.offline}> · date salvate (offline)</span>}
      </div>
    </div>
  );
};

export default CurrencyConverter;
